import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, DetailPage, Spinner } from '@nexvia-logistics/ui';
import { CaretLeft } from '@nexvia-logistics/ui/icons';
import {
  createStorageQuote,
  describeStorageError,
  getStorageProfile,
  getStorageQuotesInbox,
  type CreateStorageQuoteInput,
  type StorageRequestSummary
} from '@/shared/storage-api';
import { getStorageTypeOptions } from '@/shared/storage-services-api';
import { notifyNavCountsChanged } from '@/shared/nav-counts-signal';
import { QuoteForm } from '@/pages/QuoteForm';
import { fallbackStorageTypeLabel, QUOTE_DISABLED_REASON } from '@/pages/storage-request-format';

/** 'not-approved': `GET /storage/me` answered with an account that is not `verified` (audit S1). */
type LoadState = 'loading' | 'ready' | 'missing' | 'not-approved' | 'error';

/** The same words the list shows for the mail's link to a request that is gone (`SolicitudesPage.tsx`). */
const MISSING_FROM_MAIL_MESSAGE =
  'La solicitud del mail ya no está para cotizar: puede que ya la hayas cotizado (mirala en "Mis cotizaciones") o que el cliente la haya cerrado.';
const MISSING_MESSAGE =
  'Esta solicitud ya no está para cotizar: puede que ya la hayas cotizado (mirala en "Mis cotizaciones") o que el cliente la haya cerrado.';

/**
 * The list's query when it opened this page (`state.listSearch`, set by `SolicitudesPage`: the
 * mail's `?requestId=`), so the way back returns to the same highlighted list. Anything but a
 * query string is ignored.
 */
function listSearchFrom(state: unknown): string {
  const value = (state as { listSearch?: unknown } | null)?.listSearch;
  return typeof value === 'string' && value.startsWith('?') ? value : '';
}

/**
 * `/solicitudes/:id/cotizar` — NEX-1165 design pass (spec 2026-10-18 §3.2, decision D-2.1): the quote
 * form as a page instead of a dialog over the list. The request comes from the same
 * `GET /storage/quotes/inbox` the list reads (there is no single-request endpoint for a depósito);
 * when the id is not there (already quoted, closed, or never in this depósito's zone) the page says
 * so and offers the way back, never a blank screen. On success it replaces itself in the history
 * with the list (Back does not reopen the spent form), which shows "Enviaste tu cotización…" from
 * `location.state.notice`.
 *
 * Audit S1 (review fix) — the account's status (`GET /storage/me`) is read with the inbox, as the
 * list does: an account that is not `verified` gets the list's reason instead of a form the API
 * would refuse with 403 `STORAGE_NOT_APPROVED`. Best effort: if the status cannot be read the form
 * shows, and the API stays the guard.
 *
 * The storage type's catalog label is best effort, like the list's: without the catalog the seeded
 * codes fall back to their own label and any other code shows as sent.
 */
export default function CotizarPage() {
  const { id = '' } = useParams();
  const requestId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const listSearch = listSearchFrom(location.state);
  const backTo = `/solicitudes${listSearch}`;
  const fromMail = new URLSearchParams(listSearch).get('requestId') === id;
  const [state, setState] = useState<LoadState>('loading');
  const [request, setRequest] = useState<StorageRequestSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [storageTypeLabels, setStorageTypeLabels] = useState<Map<string, string>>(() => new Map());

  useEffect(() => {
    let cancelled = false;
    // Another id under the same route: never leave the previous request's form or notice up.
    setState('loading');
    setRequest(null);
    setError(null);
    Promise.all([
      getStorageQuotesInbox().then(
        (requests) => ({ requests }),
        (err: unknown) => ({ err })
      ),
      getStorageProfile().then(
        (profile) => profile.approvalStatus,
        () => null
      )
    ]).then(([inbox, approvalStatus]) => {
      if (cancelled) return;
      if (approvalStatus && approvalStatus !== 'verified') {
        setState('not-approved');
        return;
      }
      if ('err' in inbox) {
        setError(describeStorageError(inbox.err, 'No pudimos cargar la solicitud.'));
        setState('error');
        return;
      }
      const found = inbox.requests.find((item) => item.id === requestId) ?? null;
      setRequest(found);
      setState(found ? 'ready' : 'missing');
    });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  useEffect(() => {
    let cancelled = false;
    getStorageTypeOptions()
      .catch(() => [])
      .then((options) => {
        if (!cancelled) setStorageTypeLabels(new Map(options.map((option) => [option.code, option.label])));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const backToList = () => navigate(backTo);

  // DetailPage has no slot above its title: `order-first` lifts the link there visually (the page is
  // a flex column) while it stays after the heading in the DOM.
  const backLink = (
    <Link
      to={backTo}
      className="order-first inline-flex items-center gap-1 self-start rounded-card text-ui-label text-primary no-underline hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <CaretLeft size={14} />
      Volver a solicitudes
    </Link>
  );

  if (state === 'ready' && request) {
    const handleSubmit = async (input: CreateStorageQuoteInput) => {
      await createStorageQuote(request.id, input);
      // The request left the inbox and a quote joined Mis cotizaciones: both sidebar badges change.
      notifyNavCountsChanged();
      // The plain list, not `backTo`: the quoted request has left the inbox, and the mail's query
      // would only announce it as gone.
      navigate('/solicitudes', { replace: true, state: { notice: 'quote-sent' } });
    };
    return (
      <QuoteForm
        key={request.id}
        request={request}
        storageTypeLabel={storageTypeLabels.get(request.storageType) ?? fallbackStorageTypeLabel(request.storageType)}
        onSubmit={handleSubmit}
        onCancel={backToList}
        navigation={backLink}
      />
    );
  }

  return (
    <DetailPage title={`Cotizar la solicitud #${id}`} navigation={backLink}>
      {state === 'loading' && (
        <p className="m-0 flex items-center gap-2 text-ui-body text-on-surface-variant">
          <Spinner size={18} label={null} />
          Cargando la solicitud…
        </p>
      )}
      {state === 'error' && <Alert tone="critical">{error}</Alert>}
      {state === 'not-approved' && <Alert tone="caution">{QUOTE_DISABLED_REASON}</Alert>}
      {state === 'missing' && (
        <>
          <Alert tone="info">{fromMail ? MISSING_FROM_MAIL_MESSAGE : MISSING_MESSAGE}</Alert>
          <div>
            <Button type="button" variant="outline" size="xs" showArrow={false} onClick={backToList}>
              Volver a solicitudes
            </Button>
          </div>
        </>
      )}
    </DetailPage>
  );
}
